import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import styles from './ArtifactView.module.css';
import type { Artifact } from '../schemas';

export function ArtifactView({ artifact }: { artifact: Artifact }) {
  if (artifact.mime_type === 'text/markdown') {
    const rendered = marked.parse(artifact.content, { async: false });
    const html = DOMPurify.sanitize(rendered);
    return <div className={styles.markdown} dangerouslySetInnerHTML={{ __html: html }} />;
  }
  if (artifact.mime_type === 'application/json') {
    let pretty = artifact.content;
    try {
      pretty = JSON.stringify(JSON.parse(artifact.content), null, 2);
    } catch {
      /* keep raw */
    }
    return <pre className={styles.pre}>{pretty}</pre>;
  }
  if (artifact.mime_type === 'text/csv') {
    return <CsvTable content={artifact.content} />;
  }
  if (artifact.mime_type === 'text/html') {
    // Sandboxed iframe — strict (no scripts, no same-origin, no top nav,
    // no form submission). Aria's HTML deliverables render with full
    // style isolation but can't execute scripts or escape the frame.
    return (
      <iframe
        className={styles.htmlFrame}
        sandbox=""
        srcDoc={artifact.content}
        title={artifact.parameter_name ?? 'HTML output'}
      />
    );
  }
  return <pre className={styles.pre}>{artifact.content}</pre>;
}

function CsvTable({ content }: { content: string }) {
  const rows = useMemo(() => parseCsv(content), [content]);
  if (rows.length === 0) {
    return <pre className={styles.pre}>{content}</pre>;
  }
  const [header, ...body] = rows;
  return (
    <div className={styles.csvWrap}>
      <table className={styles.csv}>
        <thead>
          <tr>
            {header!.map((cell, i) => (
              <th key={i}>{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Minimal RFC-4180 CSV parser. Handles quoted fields, embedded commas,
 * embedded newlines, and `""` as an escaped quote. Returns an array of
 * rows (each a string[]). Empty input → [].
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const c = text[i]!;

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += c;
      i += 1;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ',') {
      row.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (c === '\r' && text[i + 1] === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      i += 2;
      continue;
    }
    if (c === '\n' || c === '\r') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      i += 1;
      continue;
    }
    cell += c;
    i += 1;
  }

  // Final cell — only push if there's leftover content (avoids a trailing
  // empty row when the input ends with a newline).
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
